import './SwitchCompanyDropdown.pcss';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { withRouter } from 'react-router';
import classnames from 'classnames';
import { compose } from 'recompose';
import CompanyModel from '@mm/state/model/CompanyModel';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import usersCRUDActions from '@mm/state/actions/UsersCRUDActions';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { RouterComponentProps } from '../../../../common/helpers/routes/interfaces';
import { OnClickElsewhere } from '../../../../common/components/OnClickElsewhere/OnClickElsewhere';
import {
  InjectedSafeTimeout,
  withSafeTimeout,
} from '../../../../common/hocs/withSafeTimeout';
import { useCreateCompanyWarningModal } from '../../SettingsPage/CompanyPanel/use-create-company-warning-modal';
import { DownOutlined, UpOutlined } from '@ant-design/icons';

type OuterProps = {
  companies: CompanyModel[];
  showCreateCompany?: boolean;
  className?: string;
  afterCompanySwitch?: (...params: any[]) => any;
};

type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  InjectedSafeTimeout &
  RouterComponentProps;

type Props = OuterProps & InnerProps;

const useDropdownState = (props: Props) => {
  const { safeTimeout } = props;
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownTransitionActive, setDropdownTransitionActive] =
    useState(false);

  const setDropdownOpen = (newDropdownState: boolean) => {
    if (showDropdown !== newDropdownState) {
      if (newDropdownState) {
        setShowDropdown(true);
        safeTimeout(() => setDropdownTransitionActive(true), 50);
      } else {
        setDropdownTransitionActive(false);
        safeTimeout(() => setShowDropdown(false), 120);
      }
    }
  };

  return {
    dropdownOpen: showDropdown,
    dropdownTransitionActive,
    setDropdownOpen,
  };
};

const SwitchCompanyDropdown = (props: Props) => {
  const {
    activeCompany,
    companies,
    showCreateCompany,
    className,
    activateCompany,
    session,
    afterCompanySwitch,
  } = props;
  const { dropdownOpen, setDropdownOpen, dropdownTransitionActive } =
    useDropdownState(props);
  const classList = classnames('switch-company-dropdown', className, {
    'switch-company-dropdown--dropdown-open': dropdownTransitionActive,
  });

  const connectedPageCRUDActions = useConnectedPageCRUDActions();

  const { showCreateCompanyWarningModal } = useCreateCompanyWarningModal();

  const handleClickOutside = () => {
    if (dropdownOpen) {
      setDropdownOpen(false);
    }
  };

  const handleCompanyClick = async (id: number) => {
    setDropdownOpen(false);
    await activateCompany(session.id, id);

    connectedPageCRUDActions.nullifyConnectedPage();

    if (afterCompanySwitch) {
      afterCompanySwitch();
    }
  };

  if (activeCompany) {
    return (
      <OnClickElsewhere className={classList} handleClick={handleClickOutside}>
        <div
          className="switch-company-dropdown__active"
          onClick={() => setDropdownOpen(!dropdownOpen)}
        >
          <div className="switch-company-dropdown__active-company-name">
            {activeCompany.name}
          </div>

          {dropdownOpen ? (
            <UpOutlined className="switch-company-dropdown__arrow-icon" />
          ) : (
            <DownOutlined className="switch-company-dropdown__arrow-icon" />
          )}
        </div>

        {dropdownOpen && (
          <div className="switch-company-dropdown__dropdown">
            {showCreateCompany && (
              <div
                key="add-company"
                className="switch-company-dropdown__dropdown-item"
                onClick={showCreateCompanyWarningModal}
              >
                + Create New Company
              </div>
            )}

            {companies.map(company => (
              <div
                key={company.id}
                className="switch-company-dropdown__dropdown-item"
                onClick={() => handleCompanyClick(company.id)}
              >
                {company.name}
              </div>
            ))}
          </div>
        )}
      </OnClickElsewhere>
    );
  }

  return null;
};

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  activateCompany: (entityId: number, id: number) =>
    dispatch(usersCRUDActions.updateActiveCompanyId(entityId, id)),
});

const mapStateToProps = (state: StoreState) => ({
  activeCompany: activeCompanySelector(state),
  session: state.session.session,
});

const enhance = compose<InnerProps, OuterProps>(
  withSafeTimeout,
  connect(mapStateToProps, mapDispatchToProps),
  withRouter,
);

export default enhance(SwitchCompanyDropdown);
